import { useMemo } from 'react';
import { GroupRights } from '../utils/newScheduleAvails';

type FilterArgs = {
  formatDisplayLabel: (code: string) => string;
  normalizedQuery: string;
  rightsOptions?: string[];
  showOnlyWithRights: boolean;
  selectedRightsList?: string[];
};

export const shouldDisplayAvailOption = (
  code: string,
  { formatDisplayLabel, normalizedQuery, rightsOptions, showOnlyWithRights, selectedRightsList }: FilterArgs
) => {
  if (selectedRightsList?.includes(code)) return false;

  if (showOnlyWithRights && rightsOptions && !rightsOptions.includes(code)) {
    return false;
  }

  if (!normalizedQuery) return true;

  const displayLabel = formatDisplayLabel(code);

  return displayLabel.toLowerCase().includes(normalizedQuery) || code.toLowerCase().includes(normalizedQuery);
};

type UseAvailFilteringArgs = {
  formatDisplayLabel: (code: string) => string;
  groupRights?: GroupRights[];
  isTerritoriesWithGroups: boolean;
  query: string;
  rightsList?: string[];
  rightsOptions?: string[];
  selectedRightsList?: string[];
  showOnlyWithRights?: boolean;
};

export const useAvailFiltering = ({
  formatDisplayLabel,
  groupRights,
  isTerritoriesWithGroups,
  query,
  rightsList,
  rightsOptions,
  selectedRightsList,
  showOnlyWithRights = false
}: UseAvailFilteringArgs) => {
  const normalizedQuery = useMemo(() => query.trim().toLowerCase(), [query]);

  const filteredRights = useMemo(() => {
    if (!rightsList || isTerritoriesWithGroups) return [];

    return rightsList.filter((code) =>
      shouldDisplayAvailOption(code, {
        formatDisplayLabel,
        normalizedQuery,
        rightsOptions,
        showOnlyWithRights,
        selectedRightsList
      })
    );
  }, [formatDisplayLabel, isTerritoriesWithGroups, normalizedQuery, rightsList, rightsOptions, selectedRightsList, showOnlyWithRights]);

  const filteredGroupRights = useMemo(() => {
    if (!isTerritoriesWithGroups) return [];

    return (groupRights ?? [])
      .map((group) => ({
        label: group.label,
        territories: group.territories.filter((code) =>
          shouldDisplayAvailOption(code, {
            formatDisplayLabel,
            normalizedQuery,
            rightsOptions,
            showOnlyWithRights,
            selectedRightsList
          })
        )
      }))
      .filter((group) => group.territories.length > 0);
  }, [formatDisplayLabel, groupRights, isTerritoriesWithGroups, normalizedQuery, rightsOptions, selectedRightsList, showOnlyWithRights]);

  return {
    filteredRights,
    filteredGroupRights
  };
};
